import { Facebook, Linkedin } from "lucide-react";
import { LocaleLink } from "@/components/site/LocaleLink";
import { BrandLogo } from "@/components/site/BrandLogo";
import { useT } from "@/lib/i18n/I18nProvider";
import { useConsent } from "@/lib/consent/ConsentProvider";
import { useLocaleSwitchHandler } from "@/components/site/LocaleSwitcher";
import { cn } from "@/lib/utils";
import type { MessageKey } from "@/lib/i18n/messages/en";

type Item = { label: string; to?: string; accent?: boolean };
type ItemDef = { key: MessageKey; to?: string; accent?: boolean };

const platformDefs: ItemDef[] = [
  { key: "footer.link.requestDemo", to: "/request-a-demo", accent: true },
  { key: "footer.link.mlp", to: "/media-monitoring/platform" },
  { key: "footer.link.coverage", to: "/media-monitoring/coverage" },
  { key: "footer.link.pricing", to: "/pricing" },
  { key: "footer.link.faq", to: "/media-monitoring/platform/faq" },
];
const servicesDefs: ItemDef[] = [
  { key: "footer.link.mediaAnalysis", to: "/media-analysis" },
  { key: "footer.link.reputation", to: "/media-analysis/reputation" },
  { key: "footer.link.competitive", to: "/media-analysis/competitive-intelligence" },
  { key: "footer.link.crisis", to: "/media-analysis/crisis-monitoring" },
  { key: "footer.link.market", to: "/media-analysis/market-research" },
  { key: "footer.link.employer", to: "/media-analysis/employer-branding" },
];
const resourcesDefs: ItemDef[] = [
  { key: "footer.link.blog", to: "/blog" },
  { key: "footer.link.reports", to: "/reports" },
  { key: "footer.link.whoWeHelp", to: "/who-we-help" },
  { key: "footer.link.whyPerceptica", to: "/why-perceptica" },
];
const companyDefs: ItemDef[] = [
  { key: "footer.link.about", to: "/about-us" },
  { key: "footer.link.contact", to: "/contact-us" },
  { key: "footer.link.partners", to: "/partners" },
];
const legalDefs: ItemDef[] = [
  { key: "footer.link.privacy", to: "/privacy-policy" },
  { key: "footer.link.cookies", to: "/cookie-policy" },
];

function ColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-foreground/90">
      {children}
    </h3>
  );
}

function CookieSettingsButton() {
  const { t } = useT();
  const { openPreferences } = useConsent();
  return (
    <button
      type="button"
      onClick={openPreferences}
      className="text-primary-foreground/70 transition-colors hover:text-primary-foreground"
    >
      {t("footer.cookieSettings")}
    </button>
  );
}

function FooterLink({ item, accent = false }: { item: Item; accent?: boolean }) {
  const className = accent
    ? "text-[13.5px] font-bold tracking-[-0.005em] text-accent transition-opacity hover:opacity-80"
    : "text-[13.5px] tracking-[-0.005em] text-primary-foreground/65 transition-colors duration-200 hover:text-primary-foreground";
  if (item.to) {
    return (
      <LocaleLink to={item.to} className={className}>
        {item.label}
      </LocaleLink>
    );
  }
  return (
    <a href="#" className={className}>
      {item.label}
    </a>
  );
}

function FooterColumn({ heading, items }: { heading: string; items: Item[] }) {
  return (
    <div>
      <ColumnHeading>{heading}</ColumnHeading>
      <ul className="space-y-3.5">
        {items.map((i) => (
          <li key={i.label}>
            <FooterLink item={i} accent={i.accent} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  const { t } = useT();
  const resolve = (defs: ItemDef[]): Item[] =>
    defs.map((d) => ({ label: t(d.key), to: d.to, accent: d.accent }));
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="mx-auto max-w-[1400px] px-6 pt-20 pb-8 lg:px-10">
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 lg:grid-cols-6">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <BrandLogo
              size={32}
              wordmarkClassName="text-primary-foreground"
            />
            <p className="mt-5 max-w-xs text-[13px] leading-relaxed text-primary-foreground/60">
              {t("footer.tagline")}
            </p>
            <dl className="mt-6 space-y-2 text-[12.5px] text-primary-foreground/65">
              <div className="flex gap-3"><dt className="w-6 text-primary-foreground/70">E:</dt><dd>sales@perceptica.com</dd></div>
              <div className="flex gap-3"><dt className="w-6 text-primary-foreground/70">HQ:</dt><dd>{t("footer.hq.value")}</dd></div>
            </dl>
            <div className="mt-8 flex items-center gap-5 text-primary-foreground/70">
              <a
                href="https://www.facebook.com/PercepticaAnalytics"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="hover:text-primary-foreground"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.linkedin.com/company/perceptica-media-intelligence/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="hover:text-primary-foreground"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <FooterColumn heading={t("footer.col.platform")} items={resolve(platformDefs)} />
          <FooterColumn heading={t("footer.col.services")} items={resolve(servicesDefs)} />
          <FooterColumn heading={t("footer.col.resources")} items={resolve(resourcesDefs)} />
          <FooterColumn heading={t("footer.col.company")} items={resolve(companyDefs)} />
        </div>

        <div className="mt-16 border-t border-primary-foreground/10 pt-6">
          <div className="flex flex-col items-center gap-4 text-xs text-primary-foreground/55 md:grid md:grid-cols-3 md:items-center md:gap-6">
            <p className="md:justify-self-start">{t("footer.copyright")}</p>
            <nav aria-label="Legal" className="md:justify-self-center">
              <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
                {resolve(legalDefs).map((i) => (
                  <li key={i.label}><FooterLink item={i} /></li>
                ))}
                <li><CookieSettingsButton /></li>
              </ul>
            </nav>
            <div className="md:justify-self-end">
              <FooterLocaleLinks />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLocaleLinks() {
  const { locale } = useT();
  const handle = useLocaleSwitchHandler();

  const linkCls = (active: boolean) =>
    cn(
      "transition-colors",
      active
        ? "font-medium text-primary-foreground"
        : "text-primary-foreground/65 hover:text-primary-foreground",
    );

  return (
    <div className="flex items-center gap-3" role="group" aria-label="Language">
      <button
        type="button"
        aria-pressed={locale === "bg"}
        onClick={handle("bg")}
        className={linkCls(locale === "bg")}
      >
        български
      </button>
      <span className="text-primary-foreground/30">|</span>
      <button
        type="button"
        aria-pressed={locale === "en"}
        onClick={handle("en")}
        className={linkCls(locale === "en")}
      >
        English
      </button>
    </div>
  );
}
